<template>
  <div
    v-if="filteredRegistryDevices && filteredRegistryDevices.length > 0"
    class="mb-2 bg-white"
    :class="{ 'p-3 border border-translucent rounded': bordered }"
  >
    <div class="d-flex justify-content-between align-items-start w-100 my-2">
      <div>
        <h4 class="font-serif">
          {{ $t("dashboard.devices_in_registry") }}
        </h4>
        <p class="my-1 fs-9">
          {{ $t("dashboard.devices") }}:
          {{ filteredRegistryDevices.length }}
        </p>
      </div>
      <div class="d-flex gap-2 flex-shrink-0">
        <IssuanceChartButton :companyIds="[company.id!]" />
        <!-- Add new device button (Poland only) -->
        <button
          v-if="company.country === 'PL'"
          type="button"
          class="btn btn-primary btn-sm"
          @click="openAddDeviceModal"
          :disabled="registryDevicesStore.isCreating"
        >
          <FontAwesomeIcon icon="plus" class="me-1" />
          {{ $t("common.add") }}
        </button>
      </div>
    </div>
    <div class="my-1" v-if="company.country === 'LT' && reportingEnabled">
      <hr />
      <h5>{{ $t("dashboard.automaticReporting.title") }}</h5>
      <!-- Automatic reporting status display -->
      <div>
        {{ $t("common.status") }}
        <span
          v-if="isLoading"
          class="badge badge-phoenix badge-phoenix-secondary"
        >
          <FontAwesomeIcon icon="spinner" spin />
          {{ $t("common.loading") }}
        </span>
        <span
          v-else-if="
            declarationsAutomationStatus === DeclarationAutomationStatus.Active
          "
          class="badge badge-phoenix badge-phoenix-success"
        >
          <FontAwesomeIcon icon="check" />
          {{ $t("common.active") }}
        </span>
        <span
          v-else-if="
            declarationsAutomationStatus ===
            DeclarationAutomationStatus.RequiresAction
          "
          class="badge badge-phoenix badge-phoenix-warning"
        >
          <FontAwesomeIcon icon="hand" />
          {{ $t("dashboard.automaticReporting.requiresAction") }}
        </span>
        <span v-else class="badge badge-phoenix badge-phoenix-warning">
          <FontAwesomeIcon icon="clock" />
          {{ $t("common.pending") }}
        </span>
      </div>
      <!-- Automatic reporting info display -->
      <div
        v-if="
          declarationsAutomationStatus !==
            DeclarationAutomationStatus.Loading &&
          declarationsAutomationStatus !== DeclarationAutomationStatus.Pending
        "
      >
        <div>
          {{ $t("dashboard.automaticReporting.lastUpdated") }}
          <span>{{ formatDateTime(declarationsUpdatedAt) }}</span>
        </div>
        <div>
          {{ $t("dashboard.automaticReporting.lastDeclaredMonth") }}
          <span>{{ formatDate(lastDeclarationMonth) }}</span>
        </div>
        <div>
          {{ $t("dashboard.automaticReporting.lastDeclaredAmount") }}
          <span>{{ formatLargeNumber(lastDeclarationMonthAmount) }}</span>
          {{ $t("common.mwh") }}
        </div>
      </div>
      <!-- Automatic reporting warning display -->
      <div
        v-if="
          declarationsAutomationStatus ===
          DeclarationAutomationStatus.RequiresAction
        "
        class="text-warning-emphasis bg-warning-subtle rounded p-4 my-2"
      >
        <div class="pb-2">
          {{ $t("dashboard.automaticReporting.helpNeeded") }}
        </div>
        <button class="btn btn-primary" @click="openHelpCrunch">
          {{ $t("common.contactSupport") }}
        </button>
      </div>
    </div>

    <ProductionDevicesTable
      :companies="[{ ...company, id: company.id! }]"
      :registryDevices="filteredRegistryDevices"
      :companyId="company.id!"
      :ltReportingStatuses="ltDeviceStatuses"
      :showScheduledTransactions="true"
      class="pb-3"
    />

    <!-- Polish PoA Generation (Polish companies only) -->
    <div v-if="company.country === 'PL'" class="pb-3">
      <PolishPoASection
        v-for="registryDevice in filteredRegistryDevices"
        :key="registryDevice.id"
        :registryDevice
        :poaGenerationStates
        :poaGeneratedSuccessfully
        :generatePoA
        :canGeneratePoA
        :getDevicePoAStatus
      />
    </div>
  </div>

  <!-- Show add device option when no devices exist (Poland only) -->
  <div
    v-if="
      company.country === 'PL' &&
      (!filteredRegistryDevices || filteredRegistryDevices.length === 0)
    "
    class="bg-white px-3 py-4 text-center"
    :class="{ 'border border-translucent rounded': bordered }"
  >
    <div class="mb-3">
      <FontAwesomeIcon
        icon="plus-circle"
        class="text-primary"
        style="font-size: 3rem"
      />
    </div>
    <h5>{{ $t("dashboard.no_devices_in_registry") }}</h5>
    <p class="text-muted mb-3">
      {{ $t("dashboard.add_device_description") }}
    </p>
    <button
      type="button"
      class="btn btn-primary"
      @click="openAddDeviceModal"
      :disabled="registryDevicesStore.isCreating"
    >
      <FontAwesomeIcon icon="plus" class="me-1" />
      {{ $t("dashboard.add_first_device") }}
    </button>
  </div>

  <!-- Manual Device Creation Modal (Polish companies only) -->
  <ModalComponent
    v-if="company.country === 'PL'"
    ref="addDeviceModal"
    :submitButtonText="
      registryDevicesStore.isCreating
        ? $t('common.creating')
        : $t('common.create_device')
    "
    :submit="handleCreateDevice"
    size="lg"
  >
    <template #title>{{ $t("dashboard.add_registry_device") }}</template>

    <template #body>
      <div class="container-fluid">
        <div class="row">
          <div class="col-12">
            <p class="text-muted mb-3">
              {{ $t("dashboard.add_registry_device_description") }}
            </p>
          </div>
        </div>

        <!-- Device EIC -->
        <div class="row mb-3">
          <div class="col-12">
            <label class="form-label" for="device_eic">
              {{ $t("registry_device.device_code") }}
              <span class="text-danger">*</span>
            </label>
            <input
              id="device_eic"
              v-model="deviceForm.device_eic"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': v$.device_eic.$error }"
              :placeholder="$t('registry_device.device_eic_placeholder')"
              maxlength="128"
              @blur="v$.device_eic.$touch"
            />
            <div
              class="invalid-feedback"
              v-for="error of v$.device_eic.$errors"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
            <div class="form-text">
              {{ $t("registry_device.device_eic_description") }}
            </div>
          </div>
        </div>

        <!-- Device Name -->
        <div class="row mb-3">
          <div class="col-12">
            <label class="form-label" for="device_name">
              {{ $t("registry_device.device_name") }}
              <span class="text-danger">*</span>
            </label>
            <input
              id="device_name"
              v-model="deviceForm.device_name"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': v$.device_name.$error }"
              :placeholder="$t('registry_device.device_name_placeholder')"
              maxlength="100"
              @blur="v$.device_name.$touch"
            />
            <div
              class="invalid-feedback"
              v-for="error of v$.device_name.$errors"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <!-- Device Power -->
          <div class="col-md-6">
            <label class="form-label" for="device_power">
              {{ $t("registry_device.device_power") }} (kW)
              <span class="text-danger">*</span>
            </label>
            <input
              id="device_power"
              v-model.number="deviceForm.device_power"
              type="number"
              class="form-control"
              :class="{ 'is-invalid': v$.device_power.$error }"
              :placeholder="$t('registry_device.device_power_placeholder')"
              min="0"
              step="0.1"
              @blur="v$.device_power.$touch"
            />
            <div
              class="invalid-feedback"
              v-for="error of v$.device_power.$errors"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>

          <!-- Technology Code -->
          <div class="col-md-6">
            <label class="form-label" for="technology_code">
              {{ $t("registry_device.technology_code") }}
              <span class="text-danger">*</span>
            </label>
            <select
              id="technology_code"
              v-model="deviceForm.tge_technology_code"
              class="form-control"
              :class="{ 'is-invalid': v$.tge_technology_code.$error }"
              @blur="v$.tge_technology_code.$touch"
            >
              <option
                v-for="option in technologyOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <div
              class="invalid-feedback"
              v-for="error of v$.tge_technology_code.$errors"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>
        </div>

        <!-- Commercial Operation Date -->
        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label" for="commercial_operation_date">
              {{ $t("registry_device.commercial_operation_date") }}
            </label>
            <input
              id="commercial_operation_date"
              v-model="deviceForm.commercial_operation_date"
              type="date"
              class="form-control"
            />
            <div class="form-text">
              {{ $t("registry_device.commercial_operation_date_description") }}
            </div>
          </div>

          <!-- Support -->
          <div class="col-md-3">
            <label class="form-label" for="support">{{
              $t("registry_device.supported_device")
            }}</label>
            <select
              id="support"
              v-model="deviceForm.support"
              class="form-control"
            >
              <option :value="null">{{ $t("common.not_specified") }}</option>
              <option :value="true">{{ $t("common.yes") }}</option>
              <option :value="false">{{ $t("common.no") }}</option>
            </select>
          </div>

          <!-- Ecolabel Approved -->
          <div class="col-md-3">
            <label class="form-label" for="ecolabel_approved">
              {{ $t("registry_device.ecolabel_approved") }}
            </label>
            <select
              id="ecolabel_approved"
              v-model="deviceForm.ecolabel_approved"
              class="form-control"
            >
              <option :value="null">{{ $t("common.not_specified") }}</option>
              <option :value="true">{{ $t("common.yes") }}</option>
              <option :value="false">{{ $t("common.no") }}</option>
            </select>
          </div>
        </div>

        <!-- Information needed for Polish Devices (to make the issuance data PoA) -->
        <div v-if="company.country === 'PL'">
          <!-- Polish Device Fields Header -->
          <div class="row mb-3">
            <div class="col-12">
              <hr />
              <h6>{{ $t("registry_device.polish_fields.title") }}</h6>
              <p class="text-muted small mb-3">
                {{ $t("registry_device.poa.required_for_issuance") }}
              </p>
            </div>
          </div>

          <!-- Installation Address -->
          <div class="row mb-3">
            <div class="col-12">
              <label class="form-label" for="installation_address">
                {{ $t("registry_device.polish_fields.installation_address") }}
                <span class="text-danger">*</span>
              </label>
              <input
                id="installation_address"
                v-model="polishForm.installation_address"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': polishV$.installation_address?.$error }"
                :placeholder="
                  $t(
                    'registry_device.polish_fields.installation_address_placeholder'
                  )
                "
                maxlength="500"
                @blur="polishV$.installation_address?.$touch"
              />
              <div
                class="invalid-feedback"
                v-for="error of polishV$.installation_address?.$errors || []"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
              <div class="form-text">
                {{
                  $t(
                    "registry_device.polish_fields.installation_address_description"
                  )
                }}
              </div>
            </div>
          </div>

          <!-- Metering System Info -->
          <div class="row mb-3">
            <div class="col-md-8">
              <label class="form-label" for="metering_system_number">
                {{ $t("registry_device.polish_fields.metering_system_number") }}
                <span class="text-danger">*</span>
              </label>
              <input
                id="metering_system_number"
                v-model="polishForm.metering_system_number"
                type="text"
                class="form-control"
                :class="{
                  'is-invalid': polishV$.metering_system_number?.$error,
                }"
                :placeholder="
                  $t(
                    'registry_device.polish_fields.metering_system_number_placeholder'
                  )
                "
                maxlength="100"
                @blur="polishV$.metering_system_number?.$touch"
              />
              <div
                class="invalid-feedback"
                v-for="error of polishV$.metering_system_number?.$errors || []"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
              <div class="form-text">
                {{
                  $t(
                    "registry_device.polish_fields.metering_system_number_description"
                  )
                }}
              </div>
            </div>

            <div class="col-md-4">
              <label class="form-label" for="metering_system_multiplier">
                {{
                  $t("registry_device.polish_fields.metering_system_multiplier")
                }}
                <span class="text-danger">*</span>
              </label>
              <input
                id="metering_system_multiplier"
                v-model.number="polishForm.metering_system_multiplier"
                type="number"
                class="form-control"
                :class="{
                  'is-invalid': polishV$.metering_system_multiplier?.$error,
                }"
                :placeholder="
                  $t(
                    'registry_device.polish_fields.metering_system_multiplier_placeholder'
                  )
                "
                min="1"
                step="1"
                @blur="polishV$.metering_system_multiplier?.$touch"
              />
              <div
                class="invalid-feedback"
                v-for="error of polishV$.metering_system_multiplier?.$errors ||
                []"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
              <div class="form-text">
                {{
                  $t(
                    "registry_device.polish_fields.metering_system_multiplier_description"
                  )
                }}
              </div>
            </div>
          </div>

          <!-- OSD Information -->
          <div class="row mb-3">
            <div class="col-md-6">
              <label class="form-label" for="osd_name">
                {{ $t("registry_device.polish_fields.osd_name") }}
                <span class="text-danger">*</span>
              </label>
              <input
                id="osd_name"
                v-model="polishForm.osd_name"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': polishV$.osd_name?.$error }"
                :placeholder="
                  $t('registry_device.polish_fields.osd_name_placeholder')
                "
                maxlength="200"
                @blur="polishV$.osd_name?.$touch"
              />
              <div
                class="invalid-feedback"
                v-for="error of polishV$.osd_name?.$errors || []"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
              <div class="form-text">
                {{ $t("registry_device.polish_fields.osd_name_description") }}
              </div>
            </div>

            <div class="col-md-6">
              <label class="form-label" for="osd_address">
                {{ $t("registry_device.polish_fields.osd_address") }}
                <span class="text-danger">*</span>
              </label>
              <input
                id="osd_address"
                v-model="polishForm.osd_address"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': polishV$.osd_address?.$error }"
                :placeholder="
                  $t('registry_device.polish_fields.osd_address_placeholder')
                "
                maxlength="500"
                @blur="polishV$.osd_address?.$touch"
              />
              <div
                class="invalid-feedback"
                v-for="error of polishV$.osd_address?.$errors || []"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
              <div class="form-text">
                {{
                  $t("registry_device.polish_fields.osd_address_description")
                }}
              </div>
            </div>
          </div>
        </div>

        <!-- Error display -->
        <div v-if="registryDevicesStore.error" class="row">
          <div class="col-12">
            <div class="alert alert-danger" role="alert">
              <FontAwesomeIcon icon="exclamation-triangle" />
              {{ registryDevicesStore.error }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import {
  formatDate,
  formatDateTimeIntoDate,
  formatLargeNumber,
} from "@/composables/formatUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useHelpCrunch } from "@/composables/useHelpCrunch";
import { useRegistryDevicesStore } from "@/composables/useRegistryDevicesStore";
import { useValidators } from "@/composables/useValidators";
import IssuanceChartButton from "@/dashboard/consolidated/IssuanceChartButton.vue";
import ProductionDevicesTable from "@/dashboard/consolidated/ProductionDevicesTable.vue";
import type { LtReportingStatusMap } from "@/dashboard/consolidated/ProductionDevicesTable.vue";
import PolishPoASection from "@/dashboard/production_strategy/pl/components/PolishPoASection.vue";
import { usePolishDeviceValidation } from "@/dashboard/production_strategy/pl/usePolishDeviceValidation";
import { usePolishPoA } from "@/dashboard/production_strategy/pl/usePolishPoA";
import type { Company, UpsertRegistryDeviceRequest } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import useVuelidate from "@vuelidate/core";
import { computed, inject, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

type ProductionDeclaration = {
  updated_at: string;
  production_month: string;
  production_amount: number;
  registry_device_id: number;
  status: string;
};

enum DeclarationAutomationStatus {
  Loading = "loading",
  Active = "active",
  Pending = "pending",
  RequiresAction = "requires_action",
}

const props = withDefaults(
  defineProps<{
    company: Company;
    /** Render as a standalone card (border + padding); off when embedded in a tab that already provides a card. */
    bordered?: boolean;
  }>(),
  { bordered: false }
);

const { t } = useI18n();
const validators = useValidators(t);
const reportingEnabled = ref<boolean>(true);

// Initialize Polish composables
const polishValidation = usePolishDeviceValidation(props.company);
const polishPoA = usePolishPoA(props.company);

const isLoading = ref<boolean>(true);
const esoMappings = ref<Map<string, boolean>>(new Map());
const declarationsMap = ref<Map<number, ProductionDeclaration>>(new Map());
const { openHelpCrunch } = useHelpCrunch();
const globalErrorHandler = inject("globalErrorHandler") as (error: any) => void;

const ltDeviceStatuses = computed<LtReportingStatusMap | undefined>(() => {
  if (props.company.country !== "LT") return undefined;
  const statuses: LtReportingStatusMap = new Map();
  for (const device of filteredRegistryDevices.value) {
    if (!esoMappings.value.get(device.device_eic)) {
      statuses.set(device.id, "missing_association");
    } else {
      const declaration = declarationsMap.value.get(device.id);
      if (!declaration) {
        statuses.set(device.id, "declared");
      } else if (declaration.status === "pending_approval") {
        statuses.set(device.id, "pending_approval");
      } else if (declaration.status === "rejected") {
        statuses.set(device.id, "rejected");
      } else {
        statuses.set(device.id, "declared");
      }
    }
  }
  return statuses;
});

// Registry device store and modal state
const registryDevicesStore = useRegistryDevicesStore();
const addDeviceModal =
  useTemplateRef<InstanceType<typeof ModalComponent>>("addDeviceModal");

// Production estimate override (handled by DeviceProjectionModal inside ProductionDevicesTable)

// Device form state
const deviceForm = ref<UpsertRegistryDeviceRequest>({
  device_eic: "",
  device_name: "",
  device_power: 0,
  tge_technology_code: "",
  commercial_operation_date: null,
  support: null,
  ecolabel_approved: null,
});

// Validation rules
const rules = computed(() => ({
  device_eic: { required: validators.required },
  device_name: { required: validators.required },
  device_power: {
    required: validators.required,
    minValue: validators.min(0.1),
  },
  tge_technology_code: { required: validators.required },
}));

// Vuelidate validation (Polish validation handled by composable)
const v$ = useVuelidate(rules, deviceForm);

// Technology options for Polish market (TGE codes)
const technologyOptions = computed(() => [
  { value: "", label: t("common.select_option") },
  { value: "PVA", label: "PVA: " + t("common.technology.solar") },
  { value: "WIL", label: "WIL: " + t("common.technology.wind") },
  { value: "WOA", label: "WOA: " + t("common.technology.hydro") + " < 0.3 MW" },
  { value: "WOB", label: "WOB: " + t("common.technology.hydro") + " < 1 MW" },
  { value: "WOC", label: "WOC: " + t("common.technology.hydro") + " < 5 MW" },
  { value: "WOD", label: "WOD: " + t("common.technology.hydro") + " < 10 MW" },
  { value: "WOE", label: "WOE: " + t("common.technology.hydro") + " > 10 MW" },
  {
    value: "BGM",
    label: "BGM: " + t("common.technology.biomass") + " - biogas mixed",
  },
  {
    value: "BGR",
    label: "BGR: " + t("common.technology.biomass") + " - biogas agricultural",
  },
  {
    value: "BGO",
    label: "BGO: " + t("common.technology.biomass") + " - biogas sewage",
  },
  {
    value: "BGS",
    label: "BGS: " + t("common.technology.biomass") + " - biogas landfill",
  },
  {
    value: "BMM",
    label: "BMM: " + t("common.technology.biomass") + " - mixed",
  },
  {
    value: "BME",
    label: "BME: " + t("common.technology.biomass") + " - energy crops",
  },
  {
    value: "BMG",
    label: "BMG: " + t("common.technology.biomass") + " - forest/agricultural",
  },
  {
    value: "BMP",
    label: "BMP: " + t("common.technology.biomass") + " - industrial wood",
  },
  {
    value: "ITPO",
    label: "ITPO: " + t("common.technology.biomass") + " - thermal waste",
  },
  {
    value: "WSB",
    label: "WSB: " + t("common.technology.biomass") + " - co-firing biomass",
  },
  {
    value: "WSG",
    label: "WSG: " + t("common.technology.biomass") + " - co-firing biogas",
  },
]);

const filteredRegistryDevices = computed(() => {
  return registryDevicesStore.getFilteredDevices(
    props.company.registry_excluded_devices
  );
});

const declarationsAutomationStatus = computed(() => {
  // Only calculate status for Lithuanian companies
  if (props.company.country !== "LT") {
    return null;
  }

  // Don't show any status while loading
  if (isLoading.value) {
    return DeclarationAutomationStatus.Loading;
  }

  // Once loaded, determine the actual status
  if (declarationsMap.value.size > 0 && esoMappings.value.size > 0) {
    if (declarationsMap.value.size === filteredRegistryDevices.value.length) {
      return DeclarationAutomationStatus.Active;
    } else {
      return DeclarationAutomationStatus.RequiresAction;
    }
  }
  return DeclarationAutomationStatus.Pending;
});

const declarationsUpdatedAt = computed(() => {
  if (declarationsMap.value.size > 0) {
    const lastDeclaration = Array.from(declarationsMap.value.values()).sort(
      (a, b) => a.updated_at.localeCompare(b.updated_at)
    )[0];
    return lastDeclaration.updated_at;
  }
  return null;
});

const lastDeclarationMonth = computed(() => {
  if (declarationsMap.value.size > 0) {
    const lastDeclaration = Array.from(declarationsMap.value.values()).sort(
      (a, b) => a.updated_at.localeCompare(b.updated_at)
    )[0];
    return lastDeclaration.production_month;
  }
  return null;
});

const lastDeclarationMonthAmount = computed(() => {
  // Sum all amounts declared in the last month declared
  if (lastDeclarationMonth.value) {
    return Array.from(declarationsMap.value.values()).reduce(
      (acc, declaration) => {
        if (declaration.production_month === lastDeclarationMonth.value) {
          return acc + declaration.production_amount;
        }
        return acc;
      },
      0
    );
  }
  return 0;
});

function processDeclarationsToMap(
  productionDeclarations: ProductionDeclaration[]
) {
  const newDeclarationsMap = new Map<number, ProductionDeclaration>();
  productionDeclarations.forEach((declaration) => {
    if (newDeclarationsMap.has(declaration.registry_device_id)) {
      const competingDeclaration = newDeclarationsMap.get(
        declaration.registry_device_id
      );
      if (
        competingDeclaration &&
        competingDeclaration.production_month < declaration.production_month
      ) {
        newDeclarationsMap.set(declaration.registry_device_id, declaration);
      }
    } else {
      newDeclarationsMap.set(declaration.registry_device_id, declaration);
    }
  });
  return newDeclarationsMap;
}

const fetchProductionDeclarations = async () => {
  // Only fetch for Lithuanian companies
  if (props.company.country !== "LT" || !reportingEnabled.value) {
    isLoading.value = false;
    return;
  }

  try {
    const responseReq = await goSolidApi.get<ProductionDeclaration[]>(
      `/api/grexel/production-declarations/${props.company.id}`
    );
    const responseMappingReq = await goSolidApi.get<any>(
      `/api/integration/grexel/get-eso-mappings/${props.company.id}`
    );

    const [response, responseMapping] = await Promise.all([
      responseReq,
      responseMappingReq,
    ]);

    for (const d of responseMapping.data) {
      esoMappings.value.set(d.grid_reference, d.active);
    }

    declarationsMap.value = processDeclarationsToMap(response.data);
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

// Form validation function using Vuelidate
const validateForm = async (): Promise<boolean> => {
  const isDeviceFormValid = await v$.value.$validate();
  if (!isDeviceFormValid) {
    v$.value.$touch();
  }

  // Validate Polish fields using composable
  const isPolishFormValid = await polishValidation.validatePolishForm();

  return isDeviceFormValid && isPolishFormValid;
};

// Reset form to initial state
const resetForm = () => {
  deviceForm.value = {
    device_eic: "",
    device_name: "",
    device_power: 0,
    tge_technology_code: "",
    commercial_operation_date: null,
    support: null,
    ecolabel_approved: null,
  };
  polishValidation.resetPolishForm();
  v$.value.$reset();
  registryDevicesStore.clearError();
};

// Modal control functions
const openAddDeviceModal = () => {
  resetForm();
  addDeviceModal.value?.toggleModal();
};

const closeAddDeviceModal = () => {
  addDeviceModal.value?.toggleModal();
  resetForm();
};

// Create device handler
const handleCreateDevice = async () => {
  const isValid = await validateForm();
  if (!isValid) {
    return;
  }

  await registryDevicesStore.upsertRegistryDevice(props.company.id, {
    ...deviceForm.value,
    country_specific_metadata: polishValidation.polishForm.value,
  });

  // Close modal after successful upsert
  closeAddDeviceModal();
};

// Create alias for template usage
const formatDateTime = formatDateTimeIntoDate;

// Expose Polish composable variables for template usage
const polishForm = polishValidation.polishForm;
const polishV$ = polishValidation.polishV$;
const poaGenerationStates = polishPoA.poaGenerationStates;
const poaGeneratedSuccessfully = polishPoA.poaGeneratedSuccessfully;
const generatePoA = polishPoA.generatePoA;
const canGeneratePoA = polishPoA.canGeneratePoA;
const getDevicePoAStatus = polishPoA.getDevicePoAStatus;

onMounted(() => {
  fetchProductionDeclarations();
});
</script>
